<template>
  <v-main>
    <v-container fluid>
      <!-- Statistics Cards -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="3" rounded="xxl">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption">Total Requests</div>
                <div class="text-h5 font-weight-bold">{{ statistics.total }}</div>
              </div>
              <v-avatar color="primary" size="42">
                <v-icon color="white">mdi-account-minus</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="3" rounded="xxl">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption">Pending</div>
                <div class="text-h5 font-weight-bold warning--text">{{ statistics.pending }}</div>
              </div>
              <v-avatar color="warning" size="42">
                <v-icon color="white">mdi-clock-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="3" rounded="xxl">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption">Approved</div>
                <div class="text-h5 font-weight-bold success--text">{{ statistics.approved }}</div>
              </div>
              <v-avatar color="success" size="42">
                <v-icon color="white">mdi-check</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="3" rounded="xxl">
            <v-card-text class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption">Rejected</div>
                <div class="text-h5 font-weight-bold error--text">{{ statistics.rejected }}</div>
              </div>
              <v-avatar color="error" size="42">
                <v-icon color="white">mdi-close</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Account Delete Requests</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by name, email, phone..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    clearable
                    label="Status"
                    @click:clear="(filter.status = null), (page = 1)"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-filter-variant"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-text>
              <section
                v-if="isLoading"
                class="d-flex align-center justify-center"
                style="height: 560px"
              >
                <v-img
                  alt="Loading"
                  class="mt-1"
                  contain
                  :src="require('../../assets/loader.gif')"
                  width="110"
                  height="110"
                />
              </section>
              <v-simple-table v-else fixed-header height="560" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start font-weight-bold text-subtitle-2 sr_no">Sr No</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Name</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Email</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Phone</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Reason</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Status</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Date</th>
                      <th class="text-start font-weight-bold text-subtitle-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="account_delete_request.length != 0">
                    <tr
                      v-for="(log, i) in account_delete_request"
                      :key="`log_${i}`"
                    >
                      <td style="min-width: 85px">{{ serialNoStarting + i + 1 }}</td>
                      <td>{{ log.name || '-' }}</td>
                      <td>{{ log.email || '-' }}</td>
                      <td>{{ log.phone || '-' }}</td>
                      <td>
                        <span
                          class="d-inline-block text-truncate"
                          style="max-width: 200px"
                          :title="log.reason"
                        >
                          {{ log.reason || '-' }}
                        </span>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getStatusColor(log.status)"
                          text-color="white"
                        >
                          {{ log.status }}
                        </v-chip>
                      </td>
                      <td>{{ formatDate(log.created_at) }}</td>
                      <td>
                        <template v-if="log.status === 'pending'">
                          <v-btn
                            icon
                            small
                            color="success"
                            @click="openApprove(log)"
                          >
                            <v-icon>mdi-check</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            small
                            color="error"
                            @click="openReject(log)"
                          >
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </template>
                        <span v-else class="text-caption">
                          {{ log.processor ? `by ${log.processor.first_name || ''} ${log.processor.last_name || ''}` : '' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="8" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Approve Dialog -->
      <v-dialog v-model="approveDialog" max-width="500" persistent>
        <v-card rounded="xxl">
          <v-card-title class="font-weight-bold">
            <v-icon color="success" left>mdi-check-circle</v-icon>
            Approve Delete Request
          </v-card-title>
          <v-card-text>
            <p class="mb-2">
              Approving account deletion for
              <strong>{{ selectedRequest ? selectedRequest.name : '' }}</strong>
            </p>
            <p class="error--text text-caption mb-4">
              This will deactivate the user's account.
            </p>
            <v-textarea
              v-model="approveRemarks"
              label="Admin Remarks (Optional)"
              outlined
              rows="3"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="approveDialog = false">Cancel</v-btn>
            <v-btn
              color="success"
              :loading="processing"
              @click="confirmApprove"
            >
              Approve &amp; Delete Account
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reject Dialog -->
      <v-dialog v-model="rejectDialog" max-width="500" persistent>
        <v-card rounded="xxl">
          <v-card-title class="font-weight-bold">
            <v-icon color="error" left>mdi-close-circle</v-icon>
            Reject Delete Request
          </v-card-title>
          <v-card-text>
            <p class="mb-4">
              Rejecting account deletion for
              <strong>{{ selectedRequest ? selectedRequest.name : '' }}</strong>
            </p>
            <v-textarea
              v-model="rejectRemarks"
              label="Reason for Rejection *"
              placeholder="Please provide a reason..."
              outlined
              rows="4"
              :error-messages="rejectError"
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="rejectDialog = false">Cancel</v-btn>
            <v-btn
              color="error"
              :loading="processing"
              :disabled="!rejectRemarks"
              @click="confirmReject"
            >
              Reject Request
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import moment from "moment";
export default {
  name: "DeleteRequestPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "",
      },
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Approved", value: "approved" },
        { text: "Rejected", value: "rejected" },
      ],
      account_delete_request: [],
      statistics: { total: 0, pending: 0, approved: 0, rejected: 0 },
      page: 1,
      rowsPerPage: 10,
      serialNoStarting: 0,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      approveDialog: false,
      rejectDialog: false,
      selectedRequest: null,
      approveRemarks: "",
      rejectRemarks: "",
      rejectError: "",
      processing: false,
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD MMM YYYY, hh:mm A");
    },
    getStatusColor(status) {
      if (status === "pending") return "warning";
      if (status === "approved") return "success";
      if (status === "rejected") return "error";
      return "grey";
    },
    async getData() {
      this.isLoading = true;
      const search = this.filter.search_keyword || "";
      const status = this.filter.status || "";
      const query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        encodeURIComponent(search) +
        "&status=" +
        encodeURIComponent(status);
      try {
        const response = await axios.get(`account_delete_request?${query}`);
        this.account_delete_request = response.data.data || [];
        this.count = response.data.count || 0;
        if (response.data.statistics) {
          this.statistics = response.data.statistics;
        }
        this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.log(error);
      }
      this.isLoading = false;
    },
    openApprove(request) {
      this.selectedRequest = request;
      this.approveRemarks = "";
      this.approveDialog = true;
    },
    openReject(request) {
      this.selectedRequest = request;
      this.rejectRemarks = "";
      this.rejectError = "";
      this.rejectDialog = true;
    },
    async confirmApprove() {
      if (!this.selectedRequest) return;
      this.processing = true;
      try {
        await axios.post(
          `account_delete_request/${this.selectedRequest.id}/approve`,
          { admin_remarks: this.approveRemarks }
        );
        this.approveDialog = false;
        this.selectedRequest = null;
        await this.getData();
      } catch (e) {
        console.log(e);
      }
      this.processing = false;
    },
    async confirmReject() {
      if (!this.selectedRequest) return;
      if (!this.rejectRemarks) {
        this.rejectError = "Reason is required";
        return;
      }
      this.processing = true;
      try {
        await axios.post(
          `account_delete_request/${this.selectedRequest.id}/reject`,
          { admin_remarks: this.rejectRemarks }
        );
        this.rejectDialog = false;
        this.selectedRequest = null;
        await this.getData();
      } catch (e) {
        console.log(e);
      }
      this.processing = false;
    },
  },
};
</script>
